import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IReactIntlProvider } from '../../../../infrastructure/react-intl-provider';
import { COMMON_ITEMS_PER_PAGE, PAGINATION_FIRST_PAGE_NUMBER } from '../../../../constants/pagination.contants';
import { IPagingViewModel } from './paging.viewmodel';

interface IPagingInit {
    pageNumber?: number;
    pageSize?: number;
    pageSizeOptions?: number[];
    totalRecords?: number;
    withChooser?: boolean;
    pageSizeStorageName?: string;
}

const defaultSettings = {
    pageSize: COMMON_ITEMS_PER_PAGE,
    pageSizeOptions: [100, 200, 300],
    pageNumber: PAGINATION_FIRST_PAGE_NUMBER,
    totalRecords: null,
    withChooser: false
};

class PagingViewModel implements IPagingViewModel {
    @mobx.observable
    private totalPages: number;
    @mobx.observable
    private totalRecords: number;
    @mobx.observable
    private currentPage: number;
    @mobx.observable
    private pageSize: number;
    @mobx.observable
    private pageSizeOptions: number[];
    @mobx.observable
    public withPageSizeChooser: boolean;
    @mobx.observable
    private pageSizeStorageName?: string;

    constructor(
        fromDTO?: IPagingInit,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.currentPage = fromDTO?.pageNumber || defaultSettings.pageNumber;
        this.totalRecords = fromDTO?.totalRecords || defaultSettings.totalRecords;
        this.withPageSizeChooser = fromDTO?.withChooser || defaultSettings.withChooser;
        this.pageSizeOptions = fromDTO?.pageSizeOptions || defaultSettings.pageSizeOptions;
        this.pageSizeStorageName = fromDTO?.pageSizeStorageName;

        if (this.pageSizeStorageName) this.pageSize = Number(localStorage.getItem(this.pageSizeStorageName));
        if (!this.pageSize || !this.pageSizeOptions.includes(this.pageSize)) this.pageSize = fromDTO?.pageSize || defaultSettings.pageSize;

        this.setTotalPagesSize();
    }

    public getCurrentPage = (): number => {
        return this.currentPage;
    };

    public getCurrentPageForBE = (): number => {
        return this.currentPage - 1;
    };

    public getPageSize = (): number => {
        return this.pageSize;
    };

    public getTotalRecords = (): number => {
        return this.totalRecords;
    };

    public getTotalPages = (): number => {
        return this.totalPages;
    };

    @mobx.action
    public next = (): void => {
        if (this.currentPage === this.totalPages) {
            return;
        }

        this.currentPage++;
    };

    @mobx.action
    public prev = (): void => {
        if (this.currentPage === 1) {
            return;
        }

        this.currentPage--;
    };

    @mobx.action
    public selectPage = (newPage: number): void => {
        if (newPage === this.currentPage) {
            return;
        }

        this.currentPage = newPage;
    };

    @mobx.action
    public setTotalRecords = (newRecords: number): void => {
        this.totalRecords = newRecords;
        this.setTotalPagesSize();
    };

    @mobx.action
    public setPageSize = (newSize: number): void => {
        if (this.pageSizeStorageName) localStorage.setItem(this.pageSizeStorageName, newSize.toString());
        this.pageSize = newSize;
        this.setTotalPagesSize();
        this.selectPage(1);
    };

    @mobx.action
    private setTotalPagesSize = (): void => {
        this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
    };

    public getChooserOptions = () => {
        return this.pageSizeOptions.map(el => ({
            label: this.intlProvider.getIntl().formatMessage({
                    description: 'Items per page label',
                    defaultMessage: '{count} per page',
                    id: 'oe.common.itemsPerPage'
                }, {
                    count: el
                }
            ),
            value: el
        }));
    };
}

export { PagingViewModel };
